import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { BeatLoader } from 'react-spinners';
import AppContext from '../context/AppContext';
import CorrectionsAPI from '../api/CorrectionsAPI';
import GamesAPI from '../api/GamesAPI';
import { GameFix, HomeSample } from '../types/CorrectionModel';
import { GroupKind } from '../types/GroupModel';
import { GamesRequestParams, getGamesUrl, parseGamesQuery } from '../types/GamesRequestParams';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchGroups } from '../reducers/groupsReducer';
import { setMoveRefForModal } from '../reducers/uiReducer';
import { analytics } from '../utils/analytics';
import { GameInfo, MoveTypeIcon, Player, Puzzle, mistakeWord } from './FixOneMove';
import GamesListItemModal from './GamesListItemModal';
import PlayOutModal from './PlayOutModal';

// The trainer for your own games: one mistake at a time, in the order you played them, game after
// game through a set you can name ("15 min | 10 games", "Games this month"…), so Previous and Next
// always go somewhere you can predict.
export type FixSet = 'list' | 'tc' | 'month' | 'fixed' | 'game';

export function fixUrl(
  gameId: number | null,
  params: GamesRequestParams,
  set: FixSet = 'list',
): string {
  const query = new URLSearchParams(getGamesUrl({ ...params, page: 0 }).slice(1));
  if (gameId != null) query.set('gameId', String(gameId));
  query.set('set', set);
  return `/fix?${query.toString()}`;
}

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function setParams(set: FixSet, list: GamesRequestParams, fix: GameFix | null): GamesRequestParams {
  const group = list.group;
  switch (set) {
    case 'tc':
      return { group, timeControl: fix?.TimeControl ?? null };
    case 'month':
      return { group, from: firstOfMonth() };
    case 'fixed':
      return { group, repair: 'fixed' };
    default:
      return { ...list, page: 0 };
  }
}

function setLabel(set: FixSet, fix: GameFix | null): string {
  switch (set) {
    case 'tc':
      return `${fix?.TimeControl ?? 'Same time control'} games`;
    case 'month':
      return 'Games this month';
    case 'fixed':
      return 'Games I fixed before';
    case 'game':
      return 'Only this game';
    default:
      return 'Games in my list';
  }
}

// Where to land in a game once it loads: its first mistake, its last (stepping back), or one named
// by a link (?practice=<moveId>, or ?move=31w).
type Landing = 'first' | 'last' | { moveId?: number; ref?: string };

function landingIndex(fix: GameFix, landing: Landing): number {
  const samples = fix.Samples;
  if (landing === 'last') return Math.max(0, samples.length - 1);
  if (landing === 'first') return 0;
  const i = samples.findIndex((s) =>
    landing.moveId != null
      ? s.Item.MoveId === landing.moveId
      : `${s.Item.Number}${s.Item.SideToMove === 'white' ? 'w' : 'b'}` === landing.ref,
  );
  return Math.max(0, i);
}

// Every mistake in this game, in play order, with a tick on the ones already fixed. Click one to
// jump to it.
function MistakeList(props: {
  samples: HomeSample[];
  index: number;
  fixedNow: Set<number>;
  onPick: (i: number) => void;
}): React.ReactNode {
  const { samples, index, fixedNow, onPick } = props;
  const fixed = samples.filter((s) => s.Item.RepairCount > 0 || fixedNow.has(s.Item.MoveId)).length;
  return (
    <div className="sc-fix-list">
      <p className="sc-fix-list-head">
        Mistakes in this game <span>{`${fixed} of ${samples.length} fixed`}</span>
      </p>
      <ol>
        {samples.map((s, i) => {
          const item = s.Item;
          const done = item.RepairCount > 0 || fixedNow.has(item.MoveId);
          return (
            <li key={item.MoveId}>
              <button
                type="button"
                className={`sc-fix-list-row${i === index ? ' is-current' : ''}${done ? ' is-fixed' : ''}`}
                onClick={() => onPick(i)}
              >
                <MoveTypeIcon moveType={item.MoveType} />
                <span className="sc-fix-list-move">
                  {item.Number}
                  {item.SideToMove === 'white' ? '.' : '...'} {item.PlayedMove}
                </span>
                <span className="sc-fix-list-kind">{mistakeWord(item.MoveType).word}</span>
                {done ? (
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="sc-fix-list-done"
                    title="Fixed"
                  />
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function FixContainer(): React.ReactNode {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const { isAuthenticated } = useContext(AppContext);
  const { groups, loaded: groupsLoaded } = useAppSelector((state) => state.groupsReducer);
  const handles = useAppSelector((state) => state.settingsReducer.handles);

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const urlGameId = query.get('gameId') != null ? Number(query.get('gameId')) : null;
  const set = (query.get('set') as FixSet) ?? 'list';
  const parsed = useMemo(() => parseGamesQuery(query), [query]);

  // A signed-in player's games live in My Space; a link with no group means that one.
  const personal = groups.find((g) => g.Kind === GroupKind.Personal)?.Token ?? null;
  const list = useMemo<GamesRequestParams>(
    () => ({ ...parsed, group: parsed.group ?? personal }),
    [parsed, personal],
  );
  const waitingForGroup = isAuthenticated && parsed.group == null && !groupsLoaded;
  useEffect(() => {
    if (!groupsLoaded) dispatch(fetchGroups());
  }, [dispatch, groupsLoaded]);
  // Hide the home page's own trainer (it sits above #app).
  useEffect(() => {
    document.body.classList.add('sc-library-view');
  }, []);

  const [fix, setFix] = useState<GameFix | null>(null);
  const [index, setIndex] = useState(0);
  const [ids, setIds] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [end, setEnd] = useState<'start' | 'end' | null>(null);
  const [solvedCount, setSolvedCount] = useState(0);
  // Mistakes solved this visit; the server's RepairCount covers earlier ones.
  const [fixedNow, setFixedNow] = useState<Set<number>>(new Set());
  // The whole game (moves, notes), opened over the trainer.
  const [showGame, setShowGame] = useState(false);
  const landing = useRef<Landing>(
    query.get('practice') != null
      ? { moveId: Number(query.get('practice')) }
      : query.get('move') != null
        ? { ref: query.get('move')! }
        : 'first',
  );

  const go = (gameId: number, how: Landing, replace = true) => {
    landing.current = how;
    const next = new URLSearchParams(search);
    next.set('gameId', String(gameId));
    next.delete('practice');
    next.delete('move');
    navigate({ search: `?${next.toString()}` }, { replace });
  };

  // Games already fetched this visit, so stepping back and forth doesn't refetch.
  const cache = useRef(new Map<number, GameFix>());
  const load = async (gameId: number): Promise<GameFix> => {
    const hit = cache.current.get(gameId);
    if (hit != null) return hit;
    const f = await CorrectionsAPI.getGameFix(gameId);
    cache.current.set(gameId, f);
    return f;
  };

  // The game in the address bar is the one on the board.
  useEffect(() => {
    if (urlGameId == null || fix?.GameId === urlGameId) return;
    let live = true;
    load(urlGameId)
      .then((f) => {
        if (!live) return;
        setFix(f);
        setIndex(landingIndex(f, landing.current));
        setEnd(null);
        analytics.track('open_game', { game_id: f.GameId, ui_source: 'fix' });
      })
      .catch(() => live && setFix(null));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlGameId]);

  // The games the set holds, newest first, as the list shows them. "Only this game" is just it.
  const setKey = JSON.stringify([set, setParams(set, list, fix)]);
  useEffect(() => {
    if (waitingForGroup) return;
    if (set === 'game') {
      setIds(urlGameId != null ? [urlGameId] : []);
      return;
    }
    if (set === 'tc' && fix == null) return;
    let live = true;
    GamesAPI.getGameIds(setParams(set, list, fix), true).then((found) => {
      if (!live) return;
      setIds(found);
      if (urlGameId == null && found.length > 0) go(found[0], 'first');
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKey, waitingForGroup]);

  // The current game is always part of the run, even when the set would not hold it.
  const run = useMemo(() => {
    if (ids == null || urlGameId == null) return ids ?? [];
    return ids.includes(urlGameId) ? ids : [urlGameId, ...ids];
  }, [ids, urlGameId]);
  const position = urlGameId != null ? run.indexOf(urlGameId) : -1;
  const samples = fix?.Samples ?? [];

  const stepping = useRef(false);
  // The nearest game in `dir` that has mistakes to fix, landing on its first (or, stepping back
  // mistake by mistake, its last) one.
  const jumpGame = async (dir: 1 | -1, how: Landing) => {
    if (stepping.current || fix == null || fix.GameId !== urlGameId) return;
    stepping.current = true;
    setBusy(true);
    try {
      for (
        let p = position + dir, tries = 0;
        p >= 0 && p < run.length && tries < 30;
        p += dir, tries++
      ) {
        if ((await load(run[p])).Samples.length > 0) {
          go(run[p], how);
          return;
        }
      }
      setEnd(dir > 0 ? 'end' : 'start');
    } finally {
      stepping.current = false;
      setBusy(false);
    }
  };
  // The next mistake, moving on to the next game after this one's last.
  const step = (dir: 1 | -1) => {
    if (fix == null || fix.GameId !== urlGameId) return;
    if (dir > 0 && index + 1 < samples.length) return setIndex(index + 1);
    if (dir < 0 && index > 0) return setIndex(index - 1);
    return jumpGame(dir, dir > 0 ? 'first' : 'last');
  };

  const evaluateNow = async () => {
    if (urlGameId == null) return;
    setEvaluating(true);
    try {
      await GamesAPI.evaluate(urlGameId);
      cache.current.delete(urlGameId);
      const f = await load(urlGameId);
      setFix(f);
      setIndex(0);
    } finally {
      setEvaluating(false);
    }
  };

  const atStart = position <= 0 && index === 0;
  const atEnd = position >= run.length - 1 && index >= samples.length - 1;
  const listLink = `/games${getGamesUrl({ ...parsed, page: 0 }).slice(1)}`;

  const nav = (
    <div className="sc-fix-nav">
      <div className="sc-fix-nav-row">
        <Link className="sc-fix-back" to={listLink}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
          My games
        </Link>
        <label className="sc-fix-set">
          <span className="visually-hidden">Which games</span>
          <select
            className="form-select form-select-sm"
            value={set}
            onChange={(e) => {
              const next = new URLSearchParams(search);
              next.set('set', e.target.value);
              navigate({ search: `?${next.toString()}` }, { replace: true });
            }}
          >
            {(['list', 'tc', 'month', 'fixed', 'game'] as FixSet[])
              .filter((s) => (s !== 'tc' || fix?.TimeControl) && (s !== 'fixed' || isAuthenticated))
              .map((s) => (
                <option key={s} value={s}>
                  {setLabel(s, fix)}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div className="sc-fix-nav-row">
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy || atStart}
          onClick={() => step(-1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
          Previous mistake
        </Button>
        <span className="sc-fix-where">
          {samples.length > 0
            ? `${index + 1} of ${samples.length}`
            : fix?.Evaluated
              ? 'No mistakes'
              : 'Not evaluated'}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy || atEnd}
          onClick={() => step(1)}
        >
          Next mistake
          <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
        </Button>
      </div>
      {run.length > 1 ? (
        <div className="sc-fix-nav-row">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={busy || position <= 0}
            onClick={() => jumpGame(-1, 'first')}
          >
            <FontAwesomeIcon icon={faAnglesLeft} className="me-1" />
            Previous game
          </Button>
          <span className="sc-fix-where">
            {position >= 0 ? `Game ${position + 1} of ${run.length}` : ''}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={busy || position >= run.length - 1}
            onClick={() => jumpGame(1, 'first')}
          >
            Next game
            <FontAwesomeIcon icon={faAnglesRight} className="ms-1" />
          </Button>
        </div>
      ) : null}
      {end != null ? (
        <p className="sc-fix-end">
          {end === 'end'
            ? 'That was the last mistake in these games.'
            : 'That was the first mistake in these games.'}
        </p>
      ) : null}
    </div>
  );

  if (urlGameId == null) {
    return (
      <div id="container" className="sc-fix">
        {ids != null && ids.length === 0 ? (
          <div className="sc-fix-empty">
            <p className="sc-home-own-title">No evaluated games here yet.</p>
            <p className="sc-home-own-sub">
              Games from the last month are evaluated on their own. Add games or pick another set.
            </p>
            <Link className="btn btn-primary" to={listLink}>
              My games
            </Link>
          </div>
        ) : (
          <BeatLoader color="#8a8f98" />
        )}
      </div>
    );
  }

  if (fix == null || fix.GameId !== urlGameId) {
    return (
      <div id="container" className="sc-fix">
        <BeatLoader color="#8a8f98" />
      </div>
    );
  }

  // Opens the whole game at the move being fixed; closing drops the move the overlay wrote into
  // the address bar.
  const openGame = () => {
    const current = samples[index]?.Item;
    if (current != null) {
      dispatch(
        setMoveRefForModal(`${current.Number}${current.SideToMove === 'white' ? 'w' : 'b'}`),
      );
    }
    setShowGame(true);
  };
  const closeGame = () => {
    setShowGame(false);
    dispatch(setMoveRefForModal(null));
    const next = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
    if (next.has('move')) {
      next.delete('move');
      navigate({ search: `?${next.toString()}` }, { replace: true });
    }
  };

  const gameModal =
    showGame && urlGameId != null ? (
      <>
        <GamesListItemModal gameId={urlGameId} onHide={closeGame} />
        <PlayOutModal />
      </>
    ) : null;

  const sample = samples[index];
  if (sample == null) {
    return (
      <div id="container" className="sc-fix">
        {gameModal}
        <div className="sc-fix-solo">
          {nav}
          <header>
            <GameInfo
              platform={fix.Platform}
              timeControl={fix.TimeControl}
              date={fix.Date}
              link={fix.Link}
              onOpenGame={fix.Ready ? openGame : undefined}
            />
            <p className="sc-home-players">
              <Player name={fix.White} rating={fix.WhiteRating} color="white" blundered={false} />
              <span className="sc-home-vs">vs</span>
              <Player name={fix.Black} rating={fix.BlackRating} color="black" blundered={false} />
            </p>
            <h1 className="sc-home-title">
              {!fix.Ready
                ? 'This game is still being imported.'
                : !fix.Evaluated
                  ? 'This game has not been evaluated yet.'
                  : 'No mistakes to fix in this game.'}
            </h1>
            {fix.Ready && !fix.Evaluated ? (
              <Button variant="primary" disabled={evaluating} onClick={evaluateNow}>
                {evaluating ? 'Evaluating, about 10 seconds…' : 'Evaluate it now'}
              </Button>
            ) : null}
          </header>
        </div>
      </div>
    );
  }

  const mine = Object.values(handles).some(
    (h) => h != null && h.toLowerCase() === sample.Player?.toLowerCase(),
  );
  return (
    <div id="container" className="sc-fix">
      {gameModal}
      <Puzzle
        key={sample.Item.MoveId}
        sample={sample}
        mine={mine}
        authenticated={isAuthenticated}
        solvedCount={solvedCount}
        onSolved={() => {
          setSolvedCount((n) => n + 1);
          setFixedNow((prev) => new Set(prev).add(sample.Item.MoveId));
        }}
        onNext={() => step(1)}
        nav={nav}
        list={
          <MistakeList
            samples={samples}
            index={index}
            fixedNow={fixedNow}
            onPick={(i) => {
              setIndex(i);
              setEnd(null);
            }}
          />
        }
        onOpenGame={openGame}
      />
    </div>
  );
}
